import { useContext, useEffect, useState } from 'react';
import { Button, Dropdown } from 'antd';
import {
  FileExcelOutlined,
  FileTextOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { ExportableTable } from '../../../../shared/Exporter';
import ExportToFormat from '../ExportToFormat';
import './style.less';
import { Tools } from '../../../../shared';
import { ContextApp } from '../../../../contexts/ContextApp';

type ExportRow = Record<string, unknown>;
type ExportHeader = { label: string; key: string };

/**
 * Configuration for exporting a table in CSV, XLSX, or tab-delimited TXT format.
 * @property className Optional CSS class applied to the dropdown.
 * @property exportable Table columns, records, and optional output filename.
 * @property disabled When true, prevents opening the export menu and skips data preparation.
 */
export interface ExporterProps<T> {
  readonly className?: string;
  readonly exportable: ExportableTable<T>;
  readonly disabled?: boolean;
}

interface ExportMenuProps {
  readonly filename?: string;
  readonly data: ExportRow[];
  readonly headers: ExportHeader[];
}

function ExportMenu({ filename, data, headers }: Readonly<ExportMenuProps>) {
  return (
    <div className="MenuDropdown ant-dropdown-menu">
      <div key="1">
        <ExportToFormat
          filename={filename ?? 'undefined'}
          data={data}
          headers={headers}
          format="csv"
        >
          <Button type="text" icon={<FileExcelOutlined />}>
            CSV
          </Button>
        </ExportToFormat>
      </div>
      <div key="2">
        <ExportToFormat
          filename={filename ?? 'undefined'}
          data={data}
          headers={headers}
          format="xlsx"
        >
          <Button type="text" icon={<FileExcelOutlined />}>
            XLSX
          </Button>
        </ExportToFormat>
      </div>
      <div key="3">
        <ExportToFormat
          filename={filename ?? 'undefined'}
          data={data}
          headers={headers}
          format="txt"
        >
          <Button type="text" icon={<FileTextOutlined />}>
            TXT
          </Button>
        </ExportToFormat>
      </div>
    </div>
  );
}

function createExportMenuRenderer(props: Readonly<ExportMenuProps>) {
  return function renderExportMenu() {
    return <ExportMenu {...props} />;
  };
}

function ExporterDropdown<T>(props: Readonly<ExporterProps<T>>) {
  const { t } = useContext(ContextApp);
  const { exportable, className, disabled = false } = props;
  const [dataExport, setDataExport] = useState<ExportRow[]>([]);
  const [headersExport, setHeadersExport] = useState<ExportHeader[]>([]);

  const loadDataExport = (): void => {
    const dataExportLink: ExportRow[] = [];
    exportable.items.forEach((item) => {
      const row: ExportRow = {};
      exportable.columns.forEach((col) => {
        if (col.export && col.dataIndex) {
          const key = col.dataIndex.toString();
          row[key] = col.renderDataExport
            ? col.renderDataExport(item)
            : (item as Record<string, unknown>)[key];
        }
      });
      dataExportLink.push(row);
    });
    setDataExport(dataExportLink);
  };

  const loadHeadersExport = (): void => {
    const headers: ExportHeader[] = [];
    exportable.columns.forEach((col) => {
      if (col.export) {
        headers.push({
          label:
            col.titleExport ?? (typeof col.title === 'string' ? col.title : ''),
          key: col.dataIndex?.toString() ?? '',
        });
      }
    });
    setHeadersExport(headers);
  };

  useEffect(() => {
    if (!disabled) {
      loadDataExport();
      loadHeadersExport();
    }
  }, [disabled, exportable]);

  const renderExportMenu = createExportMenuRenderer({
    filename: exportable.filename,
    data: dataExport,
    headers: headersExport,
  });

  return (
    <Dropdown
      className={className}
      popupRender={renderExportMenu}
      disabled={disabled}
    >
      <Button>
        {Tools.capitalize(t('action.export'))} <DownOutlined />
      </Button>
    </Dropdown>
  );
}

export default ExporterDropdown;
